import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Settings, 
  User, 
  Bell, 
  Cloud, 
  MessageCircle, 
  Save,
  RefreshCw,
  Check,
  Building,
  Phone,
  Mail,
  MapPin,
  FileText,
  Upload,
  Palette,
  Eye,
  X,
  Image as ImageIcon,
  Package,
  Plus,
  Trash2,
  Pencil,
  Wrench
} from "lucide-react";
import { toast } from "sonner";
import { DeduplicationTab } from "../components/settings/DeduplicationTab";
import { StorageAuditTab } from "../components/settings/StorageAuditTab";
import DataStateError from "../components/DataStateError";
import { formatCurrency } from "../lib/format";
import { useConferma } from "../components/ConfirmProvider";

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const conferma = useConferma();
  // Distingue "non c'e' niente" da "il caricamento e' fallito": senza questo
  // un guasto di rete sembrava una pagina vuota.
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Services catalog
  const [servicesCatalog, setServicesCatalog] = useState([]);
  const [newService, setNewService] = useState({ provider: "", description: "", default_cost: "" });
  const [editingService, setEditingService] = useState(null);
  const [savingService, setSavingService] = useState(false);
  
  // Broker Profile
  const [brokerProfile, setBrokerProfile] = useState({
    agency_name: "",
    broker_name: "",
    phone: "",
    email: "",
    pec: "",
    address: "",
    city: "",
    cap: "",
    province: "",
    piva: "",
    rui_number: "",
    logo_url: ""
  });
  
  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    days_before_expiry: 15,
    days_before_semiannual: 15,
    telegram_enabled: true,
    email_to_broker_enabled: false,
    daily_summary_enabled: true
  });
  
  // PDF Branding Settings
  const [pdfBranding, setPdfBranding] = useState({
    show_header: true,
    show_footer: true,
    header_color: "#1E3A5F",
    footer_text: "",
    use_broker_info: true
  });
  
  // Logo upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Integration Status
  const [integrations, setIntegrations] = useState({
    drive_connected: false,
    telegram_connected: false,
    email_configured: false,
    whatsapp_configured: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoadError(false);
    setLoading(true);
    try {
      // Fetch broker profile
      const profileRes = await axios.get("/api/settings/broker");
      if (profileRes.data) {
        setBrokerProfile(prev => ({ ...prev, ...profileRes.data }));
      }
      
      // Fetch notification settings
      const notifRes = await axios.get("/api/settings/notifications");
      if (notifRes.data) {
        setNotificationSettings(prev => ({ ...prev, ...notifRes.data }));
      }
      
      // Fetch PDF branding settings
      const pdfRes = await axios.get("/api/settings/pdf-branding");
      if (pdfRes.data) {
        setPdfBranding(prev => ({ ...prev, ...pdfRes.data }));
      }
      
      // Fetch integration status
      const intRes = await axios.get("/api/settings/app-status");
      if (intRes.data) {
        setIntegrations({
          drive_connected: intRes.data.mega_connected || intRes.data.drive_configured,
          telegram_connected: intRes.data.telegram_configured,
          email_configured: intRes.data.email_configured,
          whatsapp_configured: intRes.data.whatsapp_configured
        });
      }
      
      // Fetch services catalog
      const svcRes = await axios.get("/api/services");
      if (svcRes.data) {
        setServicesCatalog(svcRes.data);
      }
    } catch (error) {
      setLoadError(true);
      toast.error("Errore nel caricamento delle impostazioni");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCatalogService = async () => {
    if (!newService.provider.trim() || !newService.description.trim()) {
      toast.error("Fornitore e descrizione sono obbligatori");
      return;
    }
    setSavingService(true);
    try {
      const res = await axios.post("/api/services", {
        provider: newService.provider.trim(),
        description: newService.description.trim(),
        default_cost: parseFloat(newService.default_cost) || 0
      });
      setServicesCatalog(prev => [...prev, res.data]);
      setNewService({ provider: "", description: "", default_cost: "" });
      toast.success("Servizio aggiunto al catalogo");
    } catch (error) {
      toast.error("Errore nell'aggiunta del servizio");
    } finally {
      setSavingService(false);
    }
  };

  const handleUpdateCatalogService = async (serviceId) => {
    if (!editingService) return;
    setSavingService(true);
    try {
      await axios.put(`/api/services/${serviceId}`, {
        provider: editingService.provider,
        description: editingService.description,
        default_cost: parseFloat(editingService.default_cost) || 0
      });
      setServicesCatalog(prev => prev.map(s => s.service_id === serviceId ? { ...s, ...editingService } : s));
      setEditingService(null);
      toast.success("Servizio aggiornato");
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    } finally {
      setSavingService(false);
    }
  };

  const handleDeleteCatalogService = async (serviceId) => {
    try {
      await axios.delete(`/api/services/${serviceId}`);
      setServicesCatalog(prev => prev.filter(s => s.service_id !== serviceId));
      toast.success("Servizio eliminato");
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.post("/api/settings/broker", brokerProfile);
      toast.success("Profilo salvato con successo");
    } catch (error) {
      toast.error("Errore nel salvataggio del profilo");
    } finally {
      setSaving(false);
    }
  };

  const savePdfBranding = async () => {
    setSaving(true);
    try {
      await axios.post("/api/settings/pdf-branding", pdfBranding);
      toast.success("Impostazioni PDF salvate con successo");
    } catch (error) {
      toast.error("Errore nel salvataggio delle impostazioni PDF");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Tipo file non supportato. Usa PNG, JPG o WebP");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File troppo grande. Massimo 2MB");
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post("/api/settings/logo/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.success) {
        setBrokerProfile(prev => ({ ...prev, logo_url: response.data.logo_url }));
        toast.success("Logo caricato con successo");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel caricamento del logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    try {
      await axios.post("/api/settings/broker", { ...brokerProfile, logo_url: "" });
      setBrokerProfile(prev => ({ ...prev, logo_url: "" }));
      toast.success("Logo rimosso");
    } catch (error) {
      toast.error("Errore nella rimozione del logo");
    }
  };

  const saveNotifications = async () => {
    setSaving(true);
    try {
      await axios.post("/api/settings/notifications", notificationSettings);
      toast.success("Impostazioni notifiche salvate");
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="settings-page-loading">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-72" />
        </div>
        
        <div className="space-y-4">
          <Skeleton className="h-10 w-full max-w-md" />
          
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32 mb-2" />
              <Skeleton className="h-4 w-64" />
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-6" data-testid="pagina-errore-caricamento">
        <DataStateError
          titolo="Non riesco a caricare le impostazioni"
          messaggio="I dati non sono andati persi: e' il collegamento che non ha risposto."
          onRetry={fetchSettings}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground tracking-tight">
          Impostazioni
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Configura il tuo profilo e le preferenze dell'applicazione
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">Profilo</span>
          </TabsTrigger>
          <TabsTrigger value="services" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            <span className="hidden sm:inline">Servizi</span>
          </TabsTrigger>
          <TabsTrigger value="pdf" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">PDF</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            <span className="hidden sm:inline">Notifiche</span>
          </TabsTrigger>
          <TabsTrigger value="integrations" className="flex items-center gap-2">
            <Cloud className="h-4 w-4" />
            <span className="hidden sm:inline">Integrazioni</span>
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="flex items-center gap-2" data-testid="tab-maintenance-general">
            <Wrench className="h-4 w-4" />
            <span className="hidden sm:inline">Manutenzione</span>
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Dati Agenzia
              </CardTitle>
              <CardDescription>
                Informazioni sulla tua agenzia di brokeraggio
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="agency_name">Nome Agenzia</Label>
                  <Input
                    id="agency_name"
                    value={brokerProfile.agency_name}
                    onChange={(e) => setBrokerProfile(prev => ({ ...prev, agency_name: e.target.value }))}
                    placeholder="Es: AssiGlobal Srl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="broker_name">Nome Broker</Label>
                  <Input
                    id="broker_name"
                    value={brokerProfile.broker_name}
                    onChange={(e) => setBrokerProfile(prev => ({ ...prev, broker_name: e.target.value }))}
                    placeholder="Es: Mario Rossi"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="piva">Partita IVA</Label>
                  <Input
                    id="piva"
                    value={brokerProfile.piva}
                    onChange={(e) => setBrokerProfile(prev => ({ ...prev, piva: e.target.value }))}
                    placeholder="Es: 01234567890"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rui_number">Numero RUI</Label>
                  <Input
                    id="rui_number"
                    value={brokerProfile.rui_number}
                    onChange={(e) => setBrokerProfile(prev => ({ ...prev, rui_number: e.target.value }))}
                    placeholder="Es: B000123456"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                Contatti
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefono</Label>
                  <Input
                    id="phone"
                    value={brokerProfile.phone}
                    onChange={(e) => setBrokerProfile(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Es: +39 333 1234567"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={brokerProfile.email}
                    onChange={(e) => setBrokerProfile(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Es: info@agenzia.it"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="pec">PEC</Label>
                  <Input
                    id="pec"
                    type="email"
                    value={brokerProfile.pec}
                    onChange={(e) => setBrokerProfile(prev => ({ ...prev, pec: e.target.value }))}
                    placeholder="Es: agenzia@pec.it"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Indirizzo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="address">Indirizzo</Label>
                <Input
                  id="address"
                  value={brokerProfile.address}
                  onChange={(e) => setBrokerProfile(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Es: Via Roma 123"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">Città</Label>
                  <Input
                    id="city"
                    value={brokerProfile.city}
                    onChange={(e) => setBrokerProfile(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="Es: Milano"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cap">CAP</Label>
                  <Input
                    id="cap"
                    value={brokerProfile.cap}
                    onChange={(e) => setBrokerProfile(prev => ({ ...prev, cap: e.target.value }))}
                    placeholder="Es: 20100"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="province">Provincia</Label>
                  <Input
                    id="province"
                    value={brokerProfile.province}
                    onChange={(e) => setBrokerProfile(prev => ({ ...prev, province: e.target.value }))}
                    placeholder="Es: MI"
                    maxLength={2}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Logo Upload Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Logo Agenzia
              </CardTitle>
              <CardDescription>
                Carica il logo da usare nei PDF esportati
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                {brokerProfile.logo_url ? (
                  <div className="relative">
                    <img 
                      src={`${axios.defaults.baseURL}${brokerProfile.logo_url.startsWith('/api') ? brokerProfile.logo_url : '/api' + brokerProfile.logo_url}`}
                      alt="Logo agenzia"
                      className="w-24 h-24 object-contain border rounded-lg bg-white p-2"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <Button 
                      variant="destructive" 
                      size="icon" 
                      className="absolute -top-2 -right-2 h-6 w-6"
                      onClick={removeLogo}
                     aria-label="Chiudi">
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/50">
                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="logo-upload" className="cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Button variant="outline" disabled={uploadingLogo} asChild>
                        <span>
                          {uploadingLogo ? (
                            <><RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Caricamento...</>
                          ) : (
                            <><Upload className="mr-2 h-4 w-4" /> Carica Logo</>
                          )}
                        </span>
                      </Button>
                    </div>
                  </Label>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={handleLogoUpload}
                    disabled={uploadingLogo}
                  />
                  <p className="text-xs text-muted-foreground">
                    Formati supportati: PNG, JPG, WebP. Max 2MB.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Button onClick={saveProfile} disabled={saving} className="w-full md:w-auto">
            {saving ? (
              <><RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Salvataggio...</>
            ) : (
              <><Save className="mr-2 h-4 w-4" /> Salva Profilo</>
            )}
          </Button>
        </TabsContent>

        {/* Services Catalog Tab */}
        <TabsContent value="services" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Catalogo Servizi Aggiuntivi
              </CardTitle>
              <CardDescription>
                Definisci i servizi aggiuntivi che offri con le polizze (es. soccorso stradale, tutela legale). 
                Saranno disponibili come menu a tendina quando aggiungi servizi a una polizza.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add new service */}
              <div className="p-4 border rounded-lg space-y-3" data-testid="add-catalog-service-form">
                <p className="text-sm font-medium">Nuovo Servizio</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="svc_provider" className="text-xs">Fornitore</Label>
                    <Input
                      id="svc_provider"
                      placeholder="Es: ACI, ConTe, Europ Assistance"
                      value={newService.provider}
                      onChange={(e) => setNewService({...newService, provider: e.target.value})}
                      data-testid="catalog-provider-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="svc_description" className="text-xs">Descrizione</Label>
                    <Input
                      id="svc_description"
                      placeholder="Es: Soccorso stradale, Tutela legale"
                      value={newService.description}
                      onChange={(e) => setNewService({...newService, description: e.target.value})}
                      data-testid="catalog-description-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="svc_cost" className="text-xs">Costo default</Label>
                    <div className="flex gap-2">
                      <Input
                        id="svc_cost"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={newService.default_cost}
                        onChange={(e) => setNewService({...newService, default_cost: e.target.value})}
                        data-testid="catalog-cost-input"
                      />
                      <Button onClick={handleAddCatalogService} disabled={savingService} data-testid="catalog-add-btn">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Services list */}
              {servicesCatalog.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p>Nessun servizio nel catalogo</p>
                  <p className="text-xs mt-1">Aggiungi il primo servizio usando il form sopra</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {servicesCatalog.map((svc) => (
                    <div key={svc.service_id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`catalog-service-${svc.service_id}`}>
                      {editingService?.service_id === svc.service_id ? (
                        <div className="flex-1 grid grid-cols-3 gap-2 mr-2">
                          <Input
                            value={editingService.provider}
                            onChange={(e) => setEditingService({...editingService, provider: e.target.value})}
                            className="text-sm"
                          />
                          <Input
                            value={editingService.description}
                            onChange={(e) => setEditingService({...editingService, description: e.target.value})}
                            className="text-sm"
                          />
                          <Input
                            type="number"
                            step="0.01"
                            value={editingService.default_cost}
                            onChange={(e) => setEditingService({...editingService, default_cost: e.target.value})}
                            className="text-sm"
                          />
                        </div>
                      ) : (
                        <div className="flex-1">
                          <span className="font-medium">{svc.provider}</span>
                          <span className="text-muted-foreground mx-2">—</span>
                          <span className="text-sm">{svc.description}</span>
                          <span className="ml-3 text-sm font-semibold">{formatCurrency(svc.default_cost)}</span>
                        </div>
                      )}
                      <div className="flex gap-1">
                        {editingService?.service_id === svc.service_id ? (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => handleUpdateCatalogService(svc.service_id)} disabled={savingService}>
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingService(null)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => setEditingService({...svc})}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteCatalogService(svc.service_id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PDF Branding Tab */}
        <TabsContent value="pdf" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Personalizzazione PDF
              </CardTitle>
              <CardDescription>
                Configura l'aspetto dei PDF esportati (report, polizze, calendario)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Mostra Intestazione</Label>
                  <p className="text-sm text-muted-foreground">
                    Aggiungi nome agenzia, logo e contatti in alto
                  </p>
                </div>
                <Switch
                  checked={pdfBranding.show_header}
                  onCheckedChange={(checked) => setPdfBranding(prev => ({ ...prev, show_header: checked }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Mostra Piè di Pagina</Label>
                  <p className="text-sm text-muted-foreground">
                    Aggiungi indirizzo, P.IVA e numeri di pagina
                  </p>
                </div>
                <Switch
                  checked={pdfBranding.show_footer}
                  onCheckedChange={(checked) => setPdfBranding(prev => ({ ...prev, show_footer: checked }))}
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Palette className="h-4 w-4" />
                  Colore Tema
                </Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={pdfBranding.header_color}
                    onChange={(e) => setPdfBranding(prev => ({ ...prev, header_color: e.target.value }))}
                    className="w-12 h-10 rounded cursor-pointer border"
                  />
                  <Input
                    value={pdfBranding.header_color}
                    onChange={(e) => setPdfBranding(prev => ({ ...prev, header_color: e.target.value }))}
                    className="w-32 font-mono uppercase"
                    placeholder="#1E3A5F"
                  />
                  <div className="flex gap-1">
                    {["#1E3A5F", "#22C55E", "#DC2626", "#7C3AED", "#0891B2"].map((color) => (
                      <button
                        key={color}
                        className={`w-8 h-8 rounded border-2 ${pdfBranding.header_color === color ? 'border-foreground' : 'border-transparent'}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setPdfBranding(prev => ({ ...prev, header_color: color }))}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Testo Piè di Pagina Personalizzato</Label>
                <Input
                  value={pdfBranding.footer_text}
                  onChange={(e) => setPdfBranding(prev => ({ ...prev, footer_text: e.target.value }))}
                  placeholder="Es: Documento generato automaticamente - Non ha valore legale"
                />
                <p className="text-xs text-muted-foreground">
                  Lascia vuoto per mostrare solo la P.IVA
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Preview Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Anteprima
              </CardTitle>
              <CardDescription>
                Esempio di come apparirà l'intestazione e il piè di pagina
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden bg-white">
                {/* Header Preview */}
                {pdfBranding.show_header && (
                  <div 
                    className="p-4 text-white flex items-center justify-between"
                    style={{ backgroundColor: pdfBranding.header_color }}
                  >
                    <div className="flex items-center gap-3">
                      {brokerProfile.logo_url && (
                        <div className="w-10 h-10 bg-white/20 rounded flex items-center justify-center">
                          <ImageIcon className="h-6 w-6" />
                        </div>
                      )}
                      <div>
                        <p className="font-bold">{brokerProfile.agency_name || "Nome Agenzia"}</p>
                        <p className="text-sm opacity-80">{brokerProfile.broker_name || "Nome Broker"}</p>
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <p>{brokerProfile.phone || "Tel: +39 XXX"}</p>
                      <p>{brokerProfile.email || "email@agenzia.it"}</p>
                    </div>
                  </div>
                )}
                
                {/* Content Placeholder */}
                <div className="p-6 space-y-3 min-h-[150px] bg-gray-50">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  <div className="h-20 bg-gray-200 rounded mt-4"></div>
                </div>
                
                {/* Footer Preview */}
                {pdfBranding.show_footer && (
                  <div className="border-t p-3 flex justify-between items-center text-xs text-gray-500">
                    <span>{brokerProfile.address ? `${brokerProfile.address} - ${brokerProfile.cap} ${brokerProfile.city}` : "Indirizzo agenzia"}</span>
                    <span>{pdfBranding.footer_text || `P.IVA: ${brokerProfile.piva || "XXXXXXXXXXX"}`}</span>
                    <span>Pag. 1</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Button onClick={savePdfBranding} disabled={saving} className="w-full md:w-auto">
            {saving ? (
              <><RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Salvataggio...</>
            ) : (
              <><Save className="mr-2 h-4 w-4" /> Salva Impostazioni PDF</>
            )}
          </Button>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Promemoria Scadenze</CardTitle>
              <CardDescription>
                Configura quando ricevere i promemoria per le scadenze
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Giorni prima della scadenza polizza</Label>
                <Select 
                  value={notificationSettings.days_before_expiry?.toString()} 
                  onValueChange={(v) => setNotificationSettings(prev => ({ ...prev, days_before_expiry: parseInt(v) }))}
                >
                  <SelectTrigger className="w-full md:w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 giorni</SelectItem>
                    <SelectItem value="10">10 giorni</SelectItem>
                    <SelectItem value="15">15 giorni</SelectItem>
                    <SelectItem value="20">20 giorni</SelectItem>
                    <SelectItem value="30">30 giorni</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Giorni prima della rata semestrale</Label>
                <Select 
                  value={notificationSettings.days_before_semiannual?.toString()} 
                  onValueChange={(v) => setNotificationSettings(prev => ({ ...prev, days_before_semiannual: parseInt(v) }))}
                >
                  <SelectTrigger className="w-full md:w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 giorni</SelectItem>
                    <SelectItem value="10">10 giorni</SelectItem>
                    <SelectItem value="15">15 giorni</SelectItem>
                    <SelectItem value="20">20 giorni</SelectItem>
                    <SelectItem value="30">30 giorni</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Canali di Notifica</CardTitle>
              <CardDescription>
                Scegli come ricevere le notifiche
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Notifiche Telegram</Label>
                  <p className="text-sm text-muted-foreground">
                    Ricevi promemoria sul tuo bot Telegram
                  </p>
                </div>
                <Switch
                  checked={notificationSettings.telegram_enabled}
                  onCheckedChange={(checked) => setNotificationSettings(prev => ({ ...prev, telegram_enabled: checked }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Riepilogo Giornaliero</Label>
                  <p className="text-sm text-muted-foreground">
                    Ricevi un riepilogo ogni mattina alle 8:30
                  </p>
                </div>
                <Switch
                  checked={notificationSettings.daily_summary_enabled}
                  onCheckedChange={(checked) => setNotificationSettings(prev => ({ ...prev, daily_summary_enabled: checked }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Email al Broker</Label>
                  <p className="text-sm text-muted-foreground">
                    Ricevi promemoria anche via email
                  </p>
                </div>
                <Switch
                  checked={notificationSettings.email_to_broker_enabled}
                  onCheckedChange={(checked) => setNotificationSettings(prev => ({ ...prev, email_to_broker_enabled: checked }))}
                />
              </div>
            </CardContent>
          </Card>

          <Button onClick={saveNotifications} disabled={saving} className="w-full md:w-auto">
            {saving ? (
              <><RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Salvataggio...</>
            ) : (
              <><Save className="mr-2 h-4 w-4" /> Salva Notifiche</>
            )}
          </Button>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Stato Integrazioni</CardTitle>
              <CardDescription>
                Verifica lo stato delle tue integrazioni
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Cloud className="h-5 w-5 text-blue-500" />
                  <div>
                    <p className="font-medium">MEGA</p>
                    <p className="text-sm text-muted-foreground">Backup documenti e dati</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                    integrations.drive_connected 
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  }`}>
                    {integrations.drive_connected ? <Check className="h-4 w-4" /> : null}
                    {integrations.drive_connected ? 'Connesso' : 'Non connesso'}
                  </div>
                  {!integrations.drive_connected && (
                    <span className="text-xs text-muted-foreground">
                      Configurato sul server (.env)
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <MessageCircle className="h-5 w-5 text-blue-400" />
                  <div>
                    <p className="font-medium">Telegram Bot</p>
                    <p className="text-sm text-muted-foreground">Notifiche e comandi vocali</p>
                  </div>
                </div>
                <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                  integrations.telegram_connected 
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                }`}>
                  {integrations.telegram_connected ? <Check className="h-4 w-4" /> : null}
                  {integrations.telegram_connected ? 'Connesso' : 'Non connesso'}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-orange-500" />
                  <div>
                    <p className="font-medium">Email (Resend)</p>
                    <p className="text-sm text-muted-foreground">Invio email automatiche ai clienti</p>
                  </div>
                </div>
                <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                  integrations.email_configured 
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                }`}>
                  {integrations.email_configured ? <Check className="h-4 w-4" /> : null}
                  {integrations.email_configured ? 'Configurato' : 'Non configurato'}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="font-medium">WhatsApp</p>
                    <p className="text-sm text-muted-foreground">Invio messaggi e promemoria ai clienti</p>
                  </div>
                </div>
                <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                  integrations.whatsapp_configured 
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                }`}>
                  {integrations.whatsapp_configured ? <Check className="h-4 w-4" /> : null}
                  {integrations.whatsapp_configured ? 'Configurato' : 'Non configurato'}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Configurazione Integrazioni</CardTitle>
              <CardDescription>
                Vai alle sezioni dedicate per configurare le integrazioni
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/backup'}>
                <Cloud className="mr-2 h-4 w-4" />
                Configura MEGA e Backup
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/email'}>
                <Mail className="mr-2 h-4 w-4" />
                Configura Email Automatiche
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/whatsapp'}>
                <Phone className="mr-2 h-4 w-4" />
                Configura WhatsApp
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={async () => {
                  if (!(await conferma({
                    titolo: "Normalizzare i nomi delle compagnie?",
                    descrizione: "Le compagnie di tutte le polizze verranno riscritte in maiuscolo e senza suffissi. L'operazione non è reversibile.",
                    pericoloso: true,
                  }))) return;
                  try {
                    const response = await axios.post('/api/policies/companies/normalize');
                    toast.success(response.data.message);
                  } catch (error) {
                    toast.error("Errore nella normalizzazione");
                  }
                }}
              >
                <Building className="mr-2 h-4 w-4" />
                Normalizza Compagnie (MAIUSCOLO)
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={async () => {
                  if (!(await conferma({
                    titolo: "Normalizzare i nomi dei clienti?",
                    descrizione: "Tutta l'anagrafica clienti verrà riscritta in maiuscolo. L'operazione non è reversibile.",
                    pericoloso: true,
                  }))) return;
                  try {
                    toast.info("Normalizzazione clienti in corso...");
                    const response = await axios.post('/api/clients/normalize');
                    toast.success(response.data.message);
                  } catch (error) {
                    toast.error("Errore nella normalizzazione clienti");
                  }
                }}
              >
                <User className="mr-2 h-4 w-4" />
                Normalizza Clienti (MAIUSCOLO)
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start text-blue-600 hover:text-blue-700"
                data-testid="sync-local-to-mega-btn"
                onClick={async () => {
                  toast.info("Controllo documenti non sincronizzati...");
                  try {
                    const st = await axios.get('/api/documents/mega-sync/status');
                    if (!st.data.pending) {
                      toast.success("Tutti i documenti sono già su MEGA");
                      return;
                    }
                    toast.info(`${st.data.pending} documenti da caricare, attendi...`);
                    const res = await axios.post('/api/documents/mega-sync?limit=200');
                    if (res.data.errors) {
                      toast.warning(`Caricati ${res.data.synced}, ${res.data.errors} non riusciti`);
                    } else {
                      toast.success(`${res.data.synced} documenti caricati su MEGA`);
                    }
                  } catch (error) {
                    toast.error(error.response?.data?.detail || "Errore durante la sincronizzazione");
                  }
                }}
              >
                <Upload className="mr-2 h-4 w-4" />
                Sincronizza Documenti Locali su MEGA
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Maintenance Tab */}
        <TabsContent value="maintenance" className="space-y-6">
          <StorageAuditTab />
          <DeduplicationTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
